// CompanyCard.js
import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CardActions,
  CardMedia,
  Grid,
  Box,
  CircularProgress,
  Avatar
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import CustomModal from "../components/common/CustomModal";
import AddCompany from "../components/MyCompany/AddCompany";
import { getBusinessProfile, getCountries, getStates } from "../services/CommonServices";
import { genericGet, genericPut } from "../services/apiServices";
import { GET_COMPANY, SET_DEFAULT_COMPANY } from "../services/apiRoutes";
import { AuthContext } from '../context/AuthContext';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import MobileScreenShareOutlinedIcon from '@mui/icons-material/MobileScreenShareOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CustomMaster from "../components/common/CustomMaster";
import MessageBox from "../components/common/CustomMessageBox";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../services/CommonServices";
import { t } from "../constants/translationHelper";


const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[1],
  },
}));
const ContentWrapper = styled('section')(({ theme }) => ({
  padding: theme.spacing(0),
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
}));


const CompanyCard = () => {
  const navigate = useNavigate();
  const [addCompanyModal, setAddCompanyModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [getAllCompany, setGetAllCompany] = useState([]);
  const [permission, setPermissions] = useState()
  const [loading, setLoading] = useState(false)
  const { login } = useContext(AuthContext)
  const [message, setMessage] = useState({
    open: false,
    text: '',
    type: ''
  });
  const businessProfile = getBusinessProfile();
  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
    let mappedPath = currentPath.replace("/alexxcansols", "/app");


    let editPermision = hasEditPermission(mappedPath, rolePermisionsData?.permissions)
    let deletePermission = hasDeletePermission(mappedPath, rolePermisionsData?.permissions)
    let createPermision = hasCreatePermission(mappedPath, rolePermisionsData?.permissions)
    let viewPermision = hasViewPermission(mappedPath, rolePermisionsData?.permissions)

    setPermissions({
      edit: editPermision,
      delete: deletePermission,
      create: createPermision,
      view: viewPermision,
    })
  }, [])

  const handleClose = () => {
    setAddCompanyModal(false);
  };

  const handleSetDefault = async (companyId) => {
    console.log(`Set default for company ID: ${companyId}`);
    setLoading(true)
    try {
      const response = await genericPut(`${SET_DEFAULT_COMPANY}/${companyId}`, {});
      if (response.data.accessToken) {
        sessionStorage.setItem('accessToken', response.data.accessToken)
        login(response.data.accessToken)
        getCompanyList();
        setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false)

    }
  };

  const getCompanyList = async () => {
    try {
      const response = await genericGet({ url: GET_COMPANY });
      let CompanyData = response.data.companies;
      if (CompanyData) {
        setGetAllCompany(CompanyData);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const getCommonData = async () => {
    const getCountriesData = await getCountries();
    if (getCountriesData) {
      setCountries(getCountriesData);
    }
  };

  useEffect(() => {
    getCommonData();
    getCompanyList();
  }, []);



  const handleClick = (companyId) => {
    console.log(`Company card clicked: ${companyId}`);

    const currentPath = location.pathname;

    // Check path and navigate accordingly
    if (currentPath.startsWith("/app")) {
      navigate(`/app/settingcompany/${companyId}`);
    }
    else if (currentPath.startsWith("/alexxcansols")) {
      navigate(`/alexxcansols/settingcompany/${companyId}`);
    }
    else if (currentPath.startsWith("/partner")) {
      navigate(`/partner/settingcompany/${companyId}`);
    }
    else {
      // fallback navigation if path does not match
      navigate(`/app/settingcompany/${companyId}`);
    }
  };




  const defaultView = 'mycompany';
  const mainContent = (
    <>
      {permission?.view && (
        <ContentWrapper style={{ marginTop: '0px' }} variant="outlined">
          <CardContent>
            <Heading variant="h4" style={{ paddingBottom: '10px', textTransform: "capitalize" }}>
              {t("registered company")}
            </Heading>
            <Grid container spacing={2}>
              {getAllCompany.map((company) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={company.companyId}>
                  <StyledCard
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 1,
                    }}
                    onClick={() => handleClick(company.companyId)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={company?.companyLogo}
                        alt="Company Logo"
                        style={{
                          width: "100px",
                          objectFit: "cover",
                          display: "block",
                          margin: "0 auto",
                          textAlign: "center",
                          color: "transparent",
                          textIndent: "10000px",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        flexGrow: 1,
                      }}
                    >
                      <CardContent>
                        <Typography variant="h5" component="div" gutterBottom sx={{
                          fontWeight: '600', display: 'flex',
                          alignItems: 'center'
                        }}>
                          <BusinessOutlinedIcon sx={{ marginRight: 1 }} />
                          {company.companyName}

                        </Typography>
                        <Typography variant="h5" component="div" gutterBottom sx={{ fontWeight: '300', display: 'flex', alignItems: 'center', fontSize: "12px" }}>
                          <MobileScreenShareOutlinedIcon sx={{ marginRight: 1 }} />
                          {company?.mobile
                            ? company?.phonecode?.phonecode
                              ? `+${company.phonecode.phonecode}${company.mobile}`
                              : company.mobile
                            : ''}
                        </Typography>
                        <Typography variant="h5" component="div" gutterBottom sx={{ fontWeight: '300', display: 'flex', alignItems: 'center', fontSize: "12px" }}>
                          <EmailOutlinedIcon sx={{ marginRight: 1 }} />
                          {company.email}
                        </Typography>
                        <Button
                          variant="contained"
                          color={company.isDefault ? 'success' : 'primary'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(company.companyId);
                          }}
                        >
                          {company.isDefault ? "Default" : "Set Default"}
                        </Button>
                      </CardContent>
                    </Box>
                  </StyledCard>
                </Grid>

              ))}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                {/* {permission?.create && ( */}
                {permission?.create && businessProfile === "business" && (

                  <StyledCard variant="outlined" sx={{ display: "flex", justifyContent: "center" }}>
                    <CardActions  >
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => setAddCompanyModal(true)}
                      >
                        {t("Add Company")}
                      </Button>
                    </CardActions>
                  </StyledCard>
                )}
              </Grid>
            </Grid>
            <CustomModal open={addCompanyModal} handleClose={handleClose}>
              <AddCompany
                handleClose={handleClose}
                countries={countries}
                getStates={getStates}
                fetchData={getCompanyList}
                setMessage={setMessage}
              />
            </CustomModal>
          </CardContent>
        </ContentWrapper>
      )}
      <MessageBox
        open={message.open}
        onClose={() => setMessage({ ...message, open: false })}
        message={message.text}
        handleClose={() => setMessage({ ...message, open: false })}
        type={message.type}
      />
    </>
  );
  return (
    <>
      <CustomMaster
        defaultView={defaultView}
        mainContent={mainContent} />
      {!permission?.view && <AccessDenied />}
    </>
  );
};

export default CompanyCard;